import React, { useContext } from 'react';
import styled from 'astroturf/react';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';
import {
  getImpressionFeedExtraData,
  getImpressionPaperType,
  getSocialImpressionSource,
} from 'js/social/ImpressionHelper';
import { FeedContext } from '../contexts/feed';

import { Body } from 'js/design_system/Body';
import { BodyLink } from 'js/design_system/link/BodyLink';
import routes from 'sitewide-js/core_pre_webpack/routes';
import { FollowableUser, NewsType, PaperType } from 'js/social/shared/types';
import AvatarBase, { Name } from 'js/social/shared/Avatar';
import { getAge } from 'js/social/shared/dateUtils';

const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const Avatar = styled(AvatarBase)<{ small?: boolean }>`
  --size: 48px;

  @include for-tablet-up {
    --size: 60px;
  }

  &.small {
    --size: 32px;

    @include for-tablet-up {
      --size: 40px;
    }
  }
`;

const Author = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
  flex: 1;
  overflow: hidden;
`;

const AuthorDetails = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
  overflow: hidden;
`;

const AuthorTitle = styled(ImpressionWatcher)`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  color: $ds-black;
`;

const AuthorName = styled(Name)`
  @import 'stylesheets/design_system/_util.scss';
  @include text-ellipsis(1, 20px);
  @import 'stylesheets/design_system/font';
  @include body__small-bold;
`;

const AuthorAdditional = styled(Body)`
  @import 'stylesheets/design_system/_util.scss';
  @include text-ellipsis(1, 18px);

  color: $ds-gray-dark;

  > *:not(:first-child)::before {
    content: '•';
    padding: 0 6px;
    color: $ds-gray-dark;
  }
`;

const AuthorAdditionalLink = styled(BodyLink)`
  color: $ds-gray-dark;
`;

const Cta = styled.div`
  display: flex;
  align-self: start;
`;

const getPaperUpdateSentence = (paperType: PaperType | null): string | null => {
  switch (paperType) {
    case 'certified_work':
      return 'Recommended';
    case 'paper_upload':
      return 'Uploaded';
    case 'bookmark':
      return `Bookmarked`;
    default:
      return null;
  }
};

interface Props {
  className?: string;
  user: FollowableUser;
  userUrl?: string;
  createdAt: string;
  newsType: NewsType;
  newsId: number | string;
  paperType: PaperType | null;
  cta?: React.ReactNode;
  isInQuotedNews?: boolean;
  feedIndex?: number;
  feedReason?: string;
  recommendationScore?: number;
}

export const Header: React.FunctionComponent<Props> = ({
  className,
  user,
  userUrl,
  createdAt,
  newsType,
  newsId,
  paperType,
  cta,
  isInQuotedNews = false,
  feedIndex,
  feedReason,
  recommendationScore,
}) => {
  const paperUpdateSentence = getPaperUpdateSentence(paperType);
  const {
    feedConfig: { page },
  } = useContext(FeedContext);
  const trackImpressions = page === 'home' && !isInQuotedNews;
  const IMPRESSION_SECTION =
    typeof feedIndex === 'number' ? `feed-${feedIndex}` : '';
  const IMPRESSION_PAPER_TYPE = getImpressionPaperType(paperType);
  const IMPRESSION_EXTRA_DATA = getImpressionFeedExtraData({
    newsType,
    newsId,
    feedIndex,
    feedReason,
    recommendationScore,
  });

  return (
    <Container className={className}>
      <Author>
        <ImpressionWatcher
          active={trackImpressions}
          source={getSocialImpressionSource(
            page,
            IMPRESSION_SECTION,
            'prof-p',
            IMPRESSION_PAPER_TYPE,
          )}
          entity_type={ImpressionEntity.User}
          entity_id={user.id}
          extra={IMPRESSION_EXTRA_DATA}
        >
          <Avatar
            className={'clickable'}
            fullName={user.displayName}
            photoUrl={user.photo}
            profileUrl={userUrl}
            small={isInQuotedNews}
          />
        </ImpressionWatcher>
        <AuthorDetails>
          <AuthorTitle
            active={trackImpressions}
            source={getSocialImpressionSource(
              page,
              IMPRESSION_SECTION,
              'prof-n',
            )}
            entity_type={ImpressionEntity.User}
            entity_id={user.id}
            extra={IMPRESSION_EXTRA_DATA}
          >
            <AuthorName href={userUrl} className="clickable">
              {user.displayName}
            </AuthorName>
          </AuthorTitle>
          {user.university != null && (
            <AuthorAdditional size="xs">
              {user.university.displayName}
            </AuthorAdditional>
          )}
          {!isInQuotedNews && (
            <AuthorAdditional size="xs">
              <AuthorAdditionalLink
                size="xs"
                href={
                  newsType === 'post'
                    ? routes.community_show_post_url({ post_id: newsId })
                    : routes.community_show_paper_update_url({
                        paper_update_id: newsId,
                      })
                }
              >
                {getAge(createdAt)}
              </AuthorAdditionalLink>
              {paperUpdateSentence && <span>{paperUpdateSentence}</span>}
            </AuthorAdditional>
          )}
        </AuthorDetails>
      </Author>
      {cta && !isInQuotedNews && <Cta>{cta}</Cta>}
    </Container>
  );
};
